import { useState, useEffect, useMemo, useCallback } from 'react'
import { Play, Download, Trash2, Loader2, Link2, Check, X, Code2, Copy } from 'lucide-react'
import { downloadVideo } from '@/lib/download'
import { track } from '@/lib/track'
import { Doc } from '../../../convex/_generated/dataModel'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { siteUrl } from '@/lib/config/site'

// Legacy video type from Supabase
interface LegacyVideo {
  id: string
  title: string | null
  prompt: string | null
  modelId: string
  status: 'pending' | 'processing' | 'succeeded' | 'failed'
  videoUrl: string | null
  thumbnailUrl: string | null
  imageUrl: string | null
  audioUrl: string | null
  credits: number
  duration: number
  aspectRatio: '16:9' | '9:16' | '1:1' | '4:5' | '4:3'
  createdAt: number
  isLegacy: true
}

// Convex video (isLegacy is optional, defaults to false)
type ConvexVideo = Doc<'videos'> & { isLegacy?: false }

// Union type for both video sources
export type VideoItem = LegacyVideo | ConvexVideo

interface VideoCardProps {
  video: VideoItem
  onDelete: (videoId: string, isLegacy?: boolean) => void
  onPlay: (video: VideoItem) => void
  isDeleting?: boolean
}

// Helper to get video ID regardless of source
function getVideoId(video: VideoItem): string {
  return 'isLegacy' in video && video.isLegacy === true ? video.id : (video as ConvexVideo)._id
}

// Helper to check if video is legacy
function isLegacyVideo(video: VideoItem): video is LegacyVideo {
  return 'isLegacy' in video && video.isLegacy === true
}

// Helper to get the share ID for a video
function getShareId(video: VideoItem): string {
  if (isLegacyVideo(video)) {
    // Legacy videos have id like "legacy_3clht" - strip the prefix
    return video.id.replace('legacy_', '')
  }
  return (video as ConvexVideo)._id
}

export function VideoCard({ video, onDelete, onPlay, isDeleting = false }: VideoCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [copied, setCopied] = useState(false)
  const [embedOpen, setEmbedOpen] = useState(false)
  const [embedCopied, setEmbedCopied] = useState(false)
  const [embedAutoplay, setEmbedAutoplay] = useState(false)
  const [embedMuted, setEmbedMuted] = useState(false)
  const [embedLoop, setEmbedLoop] = useState(false)
  const [embedControls, setEmbedControls] = useState(true)

  const { status, thumbnailUrl, videoUrl, createdAt, title, prompt } = video
  const isPending = status === 'pending'
  const isProcessing = status === 'processing'
  const isSucceeded = status === 'succeeded'
  const isFailed = status === 'failed'

  // Timer for processing videos
  useEffect(() => {
    if (!createdAt || (!isProcessing && !isPending)) {
      setElapsedTime(0)
      return
    }

    const updateElapsedTime = () => {
      const startTime = createdAt
      const currentTime = Date.now()
      const elapsedSeconds = Math.floor((currentTime - startTime) / 1000)
      setElapsedTime(Math.max(0, elapsedSeconds))
    }

    updateElapsedTime()
    const interval = setInterval(updateElapsedTime, 1000)
    return () => clearInterval(interval)
  }, [createdAt, status, isProcessing, isPending])

  const formatElapsedTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const handleDownload = async () => {
    if (!videoUrl) return
    try {
      await downloadVideo(videoUrl, `${title || prompt || 'video'}.mp4`)
    } catch (error) {
      console.error('Failed to download video:', error)
    }
  }

  const handleDelete = () => {
    onDelete(getVideoId(video), isLegacyVideo(video))
    setShowDeleteConfirm(false)
  }

  const handleShare = async () => {
    const shareId = getShareId(video)
    const shareUrl = siteUrl(`/share/${shareId}`)

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      track('video.share_link_copied', { videoId: shareId })
    } catch (error) {
      console.error('Failed to copy share link:', error)
    }
  }

  const embedCode = useMemo(() => {
    const shareId = getShareId(video)
    const params = new URLSearchParams()
    if (embedAutoplay) params.set('autoplay', '1')
    if (embedMuted) params.set('muted', '1')
    if (embedLoop) params.set('loop', '1')
    if (!embedControls) params.set('controls', '0')
    const qs = params.toString()
    const embedUrl = siteUrl(`/embed/${shareId}${qs ? `?${qs}` : ''}`)
    return `<iframe src="${embedUrl}" width="640" height="360" frameborder="0" allowfullscreen></iframe>`
  }, [video, embedAutoplay, embedMuted, embedLoop, embedControls])

  const handleCopyEmbed = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setEmbedCopied(true)
      setTimeout(() => setEmbedCopied(false), 2000)
    } catch {
      // fallback
    }
  }, [embedCode])

  const showOverlay = isPending || isProcessing || isDeleting || isFailed

  return (
    <div className="group relative overflow-hidden rounded-lg bg-card border border-border shadow-xs hover:shadow-sm transition-shadow w-full sm:w-auto sm:min-w-[300px] sm:max-w-[360px] sm:flex-1">
      <div className="relative aspect-video bg-neutral-100 overflow-hidden">
        {/* Thumbnail image or video poster */}
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={title || prompt || 'Video thumbnail'}
            className="w-full h-full object-cover"
          />
        ) : videoUrl && isSucceeded ? (
          <video
            src={videoUrl}
            className="w-full h-full object-cover pointer-events-none"
            muted
            preload="metadata"
          />
        ) : null}

        {/* Processing overlay */}
        {showOverlay && !showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="flex items-center gap-2 text-white">
              {(isPending || isProcessing || isDeleting) && (
                <Loader2 className="h-5 w-5 animate-spin" />
              )}
              <span className="text-sm font-medium">
                {isDeleting && 'Deleting...'}
                {isPending && 'Analyzing...'}
                {isProcessing && formatElapsedTime(elapsedTime)}
                {isFailed && 'Generation failed'}
              </span>
            </div>
          </div>
        )}

        {/* Delete confirmation - replaces title overlay */}
        {showDeleteConfirm && (
          <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/80 to-black/40 p-3 pt-6 z-30 animate-in fade-in duration-100">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Trash2 size={14} className="text-white shrink-0" />
                <span className="text-sm text-white font-medium truncate">Delete this video?</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-md transition-colors"
                >
                  <X size={14} />
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors disabled:opacity-50"
                >
                  {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Play button for ready videos */}
        {isSucceeded && !showDeleteConfirm && !isDeleting && videoUrl && (
          <button
            onClick={() => onPlay(video)}
            className="absolute inset-0 flex items-center justify-center group-hover:bg-black/20 transition-colors"
          >
            <div className="w-12 h-12 bg-black/50 group-hover:bg-black/70 rounded-full flex items-center justify-center transition-colors">
              <Play size={20} className="text-white ml-0.5" fill="white" />
            </div>
          </button>
        )}

        {/* Action buttons (top right) */}
        {isSucceeded && !showDeleteConfirm && !isDeleting && (
          <div className="absolute top-2 right-2 z-20 flex flex-col gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleDownload}
              className="p-1.5 bg-white/90 rounded-lg hover:bg-white transition-colors shadow-sm"
              title="Download"
            >
              <Download size={14} className="text-neutral-700" />
            </button>
            <button
              onClick={handleShare}
              className="p-1.5 bg-white/90 rounded-lg hover:bg-white transition-colors shadow-sm"
              title={copied ? "Copied!" : "Copy share link"}
            >
              {copied ? (
                <Check size={14} className="text-green-600" />
              ) : (
                <Link2 size={14} className="text-neutral-700" />
              )}
            </button>
            <button
              onClick={() => setEmbedOpen(true)}
              className="p-1.5 bg-white/90 rounded-lg hover:bg-white transition-colors shadow-sm"
              title="Embed"
            >
              <Code2 size={14} className="text-neutral-700" />
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-1.5 bg-white/90 rounded-lg hover:bg-white transition-colors shadow-sm"
              title="Delete"
            >
              <Trash2 size={14} className="text-red-500" />
            </button>
          </div>
        )}

        {/* Title overlay */}
        {(title || prompt) && !showDeleteConfirm && (
          <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent p-3 pt-8">
            <p className="text-white text-sm font-medium truncate">
              {title || prompt}
            </p>
          </div>
        )}
      </div>

      {/* Embed dialog */}
      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent className="sm:max-w-md bg-white border-neutral-200 text-neutral-900">
          <DialogHeader>
            <DialogTitle>Embed Video</DialogTitle>
            <DialogDescription className="text-neutral-500">
              Customize the embed player and copy the code.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm text-neutral-700">Autoplay</label>
                <Switch
                  checked={embedAutoplay}
                  onCheckedChange={(checked) => {
                    setEmbedAutoplay(checked)
                    if (checked && !embedMuted) setEmbedMuted(true)
                  }}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-neutral-700">Muted</label>
                <Switch
                  checked={embedMuted}
                  onCheckedChange={setEmbedMuted}
                  disabled={embedAutoplay}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-neutral-700">Loop</label>
                <Switch
                  checked={embedLoop}
                  onCheckedChange={setEmbedLoop}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-neutral-700">Show controls</label>
                <Switch
                  checked={embedControls}
                  onCheckedChange={setEmbedControls}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
              {embedAutoplay && (
                <p className="text-xs text-neutral-400">
                  Browsers require muted audio for autoplay to work.
                </p>
              )}
            </div>

            <div className="rounded-lg p-3 text-xs font-mono break-all leading-relaxed bg-neutral-100 text-neutral-600 ring-1 ring-black/5">
              {embedCode}
            </div>

            <button
              onClick={handleCopyEmbed}
              className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                embedCopied
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-neutral-900 text-white hover:bg-neutral-800'
              }`}
            >
              {embedCopied ? (
                <>
                  <Check size={14} />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={14} />
                  Copy Embed Code
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
